import { t } from "./i18n/runtime";
import { useEffect, useRef } from "react";
import { useGameStore } from "./stores/gameStore";
import { createSession } from "./network/api";
import { RestartButton } from "./ui/RestartButton";
import { connectSession } from "./network/socket";
import { NationTooltip } from "./ui/NationTooltip";
import { TradePanel } from "./ui/TradePanel";
import { ResultScreen } from "./ui/ResultScreen";
import { HudCorner } from "./ui/HudCorner";
import { Tutorial } from "./ui/Tutorial";
import { CompanyNameGate } from "./ui/CompanyNameGate";
import { WarBanner } from "./ui/WarBanner";
import { Button } from "./ui/primitives";
import { translateError, useLocale } from "./i18n";

/** Operations room layout (§16.2), built for a 1280x720 desktop baseline. */

/** Module-level so a StrictMode remount cannot open a second session. */
let booting = false;

function Toast() {
  const toast = useGameStore((s) => s.toast);
  const clearToast = useGameStore((s) => s.clearToast);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(clearToast, 3200);
    return () => window.clearTimeout(timer);
  }, [toast, clearToast]);

  if (!toast) return null;
  return (
    <div className={`toast toast--${toast.tone}`} key={toast.key}>
      {toast.text}
    </div>
  );
}

function Boot() {
  const bootError = useGameStore((s) => s.bootError);

  if (bootError) {
    return (
      <div className="boot">
        <div className="boot__error">
          <div className="boot__error-title">{t("App.front_unavailable")}</div>
          {/* The refusal arrives in English from the server or the fetch layer. */}
          <div className="boot__error-detail">{translateError(bootError)}</div>
        </div>
        <Button tone="primary" onClick={() => window.location.reload()}>
          {t("App.try_again")}
        </Button>
      </div>
    );
  }

  return (
    <div className="boot">
      <div className="boot__title">{t("App.connecting_to_the_front")}</div>
      <div className="muted">
        {t("App.opening_a_session_and_connecting_to_the")}
      </div>
    </div>
  );
}

export function App() {
  useLocale();
  const hasWorld = useGameStore((s) => s.state !== null);
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const phaserMounted = useRef(false);

  useEffect(() => {
    if (booting) return;
    booting = true;

    let dispose: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      const store = useGameStore.getState();
      const created = await createSession();
      if (!created.ok) {
        booting = false;
        store.setBootError(created.error);
        return;
      }
      store.applySnapshot(created.data.snapshot);
      // Why the session is not running what was asked for, if it is not. The
      // paid switch is simply absent when it happens, so without this the
      // player is left to guess whether that is a setting or a fault.
      store.setAiModeNote(created.data.aiModeNote ?? null);
      if (cancelled) return;
      dispose = connectSession(created.data.sessionId);
    })();

    return () => {
      cancelled = true;
      dispose?.();
      booting = false;
    };
  }, []);

  useEffect(() => {
    const host = mapHostRef.current;
    if (!host || phaserMounted.current) return;
    phaserMounted.current = true;
    let cancelled = false;
    let destroy: (() => void) | null = null;

    // Phaser accounts for nearly all of the JavaScript payload. Keep it out of
    // the operations UI chunk so the shell and session state can render while
    // the map engine is fetched and parsed in its own cacheable request.
    void import("./game/PhaserGame")
      .then(({ createPhaserGame }) => {
        if (cancelled) return;
        const handle = createPhaserGame(host);
        destroy = () => handle.destroy();
      })
      .catch(() => {
        if (cancelled) return;
        phaserMounted.current = false;
        useGameStore
          .getState()
          .showToast(t("App.the_map_engine_could_not_be_loaded"), "error");
      });

    return () => {
      cancelled = true;
      phaserMounted.current = false;
      destroy?.();
    };
  }, [hasWorld]);

  // Boot renders the error state itself; a late socket hiccup must not tear
  // down a world the player is already looking at.
  if (!hasWorld) return <Boot />;

  return (
    <div className="app-shell">
      {/*
       * The title is a mark, not a control: centred over everything, out of the
       * way of both columns. The bar it used to live in cost 58px of map height
       * to carry it and two figures, and put the clock at the far end of the
       * screen from the board it was counting down.
       */}
      <div className="app-mid">
        <div className="brand-mark">CODEX WAR BROKER</div>
        <div className="map-stack">
          <div className="map-host" ref={mapHostRef} />
          <HudCorner />
          <RestartButton />
          <NationTooltip />
        </div>

        <div className="app-side">
          <TradePanel />
        </div>
      </div>

      {/* Over both columns, not inside the map: the briefing is read before the
          board is played, and its veil has to cover the order book too. */}
      <Tutorial />
      <CompanyNameGate />
      <WarBanner />
      <Toast />
      <ResultScreen />
    </div>
  );
}
